/**
 * @nicejob-library/features interfaces
 */

import { LDClient, LDContext, LDOptions, LDSingleKindContext } from '@launchdarkly/node-server-sdk';
import { Logger } from '@nicejob/nicejob-logger';
import { IAddress } from '@nicejob-library/data-platform-implementation/entities/common';
import { ICompany } from '@nicejob-library/data-platform-implementation/entities/Company';
import { Request } from 'express';
import { FeatureFlag, FeatureFlagCheckResult } from '../../../common';
import { User } from '../../../types/entities.types';
import { IErrorsModule } from '../../../types/modules.types';

export interface IFeaturesModule {
    projectId: string;
    test_environment: boolean;
    verbose: boolean;
    config: LDOptions;
    client?: LDClient;

    // block-risky-signups flag override
    check(input: {
        // NB: this flag must be excluded from the default overload
        flag: FeatureFlag.BLOCK_RISKY_SIGNUPS;
        custom_fields: {
            email: string;
        };
    }): Promise<FeatureFlagCheckResult<FeatureFlag.BLOCK_RISKY_SIGNUPS>>;

    // Anonymous override
    check(input: {
        // NB: this flag must be excluded from the default overload
        flag: FeatureFlag.ONBOARDING_WITH_PHONE_VERIFICATION_DISABLED;
        custom_fields: {
            ip: string;
        };
    }): Promise<FeatureFlagCheckResult<'default'>>;

    check(input: {
        // NB: this flag must be excluded from the default overload
        flag: FeatureFlag.ALLOWED_TEST_RECIPIENTS;
        custom_fields: {
            email?: string;
            phone?: string;
        };
    }): Promise<FeatureFlagCheckResult<'default'>>;

    // company-only override
    check(input: {
        // NB: these flags must be excluded from the default overload
        flag:
            | FeatureFlag.ALLOW_EMAIL_LINKS
            | FeatureFlag.ALLOW_SMS_LINKS
            | FeatureFlag.CAMPAIGN_ENROLLMENT_SQL_PRECHECK
            | FeatureFlag.COMPANY_IS_VERIFIED
            | FeatureFlag.RECOMMENDATIONS
            | FeatureFlag.JOBBER_CUSTOM_FIELD
            | FeatureFlag.SEND_TEST_MESSAGE
            | FeatureFlag.USE_SUGARWISH_TEST_ACCOUNT_IN_PRODUCTION;
        company: IFeaturesCompany;
    }): Promise<FeatureFlagCheckResult<'default'>>;

    check(input: {
        // NB: this flag must be excluded from the default overload
        flag: FeatureFlag.MULTI_FACTOR_AUTHENTICATION;
    }): Promise<FeatureFlagCheckResult<'default'>>;

    // default overload
    check(input: {
        // NB: flags with overloads above must be excluded here
        flag: Exclude<
            FeatureFlag,
            | FeatureFlag.ALLOW_EMAIL_LINKS
            | FeatureFlag.ALLOW_SMS_LINKS
            | FeatureFlag.ALLOWED_TEST_RECIPIENTS
            | FeatureFlag.CAMPAIGN_ENROLLMENT_SQL_PRECHECK
            | FeatureFlag.COMPANY_IS_VERIFIED
            | FeatureFlag.RECOMMENDATIONS
            | FeatureFlag.USE_SUGARWISH_TEST_ACCOUNT_IN_PRODUCTION
            | FeatureFlag.JOBBER_CUSTOM_FIELD
            | FeatureFlag.SEND_TEST_MESSAGE
            | FeatureFlag.ONBOARDING_WITH_PHONE_VERIFICATION_DISABLED
            | FeatureFlag.MULTI_FACTOR_AUTHENTICATION
            | FeatureFlag.BLOCK_RISKY_SIGNUPS
        >;
        company: IFeaturesCompany;
        user: IFeaturesUser;
    }): Promise<FeatureFlagCheckResult<'default'>>;

    track(input: { goal: string; company: IFeaturesCompany; user: IFeaturesUser }): Promise<void>;

    hash(ld_context: LDContext): Promise<string>;

    generateClientLDUser(input: {
        company: IFeaturesCompany;
        user: IFeaturesUser;
        admin_switching_user_id: string | null;
    }): Promise<IFeaturesClientLDUSer>;

    generateClientLDSingleKindContext(input: {
        request: Request;
        context_kind: ContextKind;
    }): Promise<IFeaturesClientSingleKindContext>;
}

export type IFeaturesCustomFields = Exclude<LDContext['custom'], undefined>;

export interface IFeaturesClientLDUSer {
    data: LDContext;
    hash: string;
}

export interface IFeaturesClientSingleKindContext {
    data: LDSingleKindContext;
    hash: string;
}
export interface IFeaturesContext {
    errors: IErrorsModule;
    log: Logger;
}

export type IFeaturesCompany = Pick<
    ICompany,
    'id' | 'account_id' | 'address' | 'company_name' | 'email' | 'phone'
>;

export type IFeaturesUser = Pick<
    User,
    'id' | 'first_name' | 'last_name' | 'email' | 'phone' | 'companies' | 'accounts'
> & {
    address: Pick<IAddress, 'country' | 'state' | 'city'> | null;
};

export enum ContextKind {
    USER = 'user',
    DEVICE = 'device',
}

export enum MetricKind {
    ONBOARDING_CLIENT_SUBMIT_PHONE = 'onboarding-client-submit-phone',
    ONBOARDING_CLIENT_CLICK_BUTTON_REGISTER = 'onboarding-client-click-button-register',
}
